import { Component, OnInit, OnDestroy } from '@angular/core'
import { AdminService } from '../../../services/admin.service'
import {
    Client,
    adminClientsPath,
    ContractStatus,
    FirestoreStructure,
    Invoice,
    InvoiceStatus,
    Merchant,
    Parking,
    ParkingStatus,
    Payout,
    Subscription,
    SubscriptionRenewalStatus,
    subscriptionsPath,
    SubscriptionType,
    Term,
} from '@parkupp/core'
import { collection, collectionData, doc, docData, orderBy, query, where, Query, DocumentData } from '@angular/fire/firestore'
import * as moment from 'moment'
import { Moment } from 'moment'
import { ParkingService } from '../../../services/parking.service'
import { firstValueFrom, Subscription as RxSubscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { InvoiceService } from 'src/app/services/invoice.service'
import Swal from 'sweetalert2'
import { PayoutService } from 'src/app/services/payout.service'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'

type DashboardTab = 'overview' | 'subscriptions' | 'invoices' | 'charts'

@Component({
    selector: 'app-admin-dashboard-page',
    templateUrl: './admin-dashboard-page.component.html',
    styleUrls: ['./admin-dashboard-page.component.scss'],
})
export class AdminDashboardPageComponent implements OnInit, OnDestroy {
    // Subscriptions management - for cleanup
    private componentSubscriptions: RxSubscription[] = []
    
    // Track current enterprise key to detect changes
    private currentLoadedEnterpriseKey: string | null | undefined = undefined
    
    // Tab management - only load data when tab is selected
    activeTab: DashboardTab = 'overview'
    private loadedTabs: Set<DashboardTab> = new Set()
    
    // Loading states per tab
    loadingOverview = false
    loadingSubscriptions = false
    loadingInvoices = false
    loadingCharts = false
    
    // Legacy loading state for backwards compatibility
    loading = false
    
    // Chart data
    chartSalesData: any[] = []
    chartSalesColorScheme: any = { domain: ['#8affb7'] }
    chartMonthlyData: any[] = []
    chartMonthlyColorScheme: any = { domain: ['#ac9aff'] }
    chartBaysData: any[] = []
    chartBaysColorScheme: any = { domain: ['#6796ff'] }
    
    // Data arrays
    allSubscriptions: Subscription[] = []
    inactiveSubscriptions: Subscription[] = []
    expiringSubscriptions: Subscription[] = []
    invoicesNotPaidOut: Invoice[] = []
    invoicesWithoutBankDetails: Invoice[] = []
    noBankMerchantRefs: any = []
    
    // Counters
    activeBays = 0
    activeSubscriptions = 0
    activeSubscriptionsPayfast = 0
    activeSubscriptionsPaygate = 0
    activeSubscriptionsEft = 0
    activeSubscriptionsManual = 0
    totalParkings = 0
    totalBuildings = 0
    totalUnpublishedBuildings = 0
    users = 0
    usersWithProfile = 0
    activeUsers = 0
    
    invoiceNotPaidOutTotalAmount = 0
    invoiceNotPaidOutTotalCount = 0
    invoiceNotPaidOutAmount = 0
    invoiceNotPaidOutCount = 0
    invoiceNotPaidOutProformaAmount = 0
    invoiceNotPaidOutProformaCount = 0
    
    overdueInvoicesCount = 0
    overdueInvoicesTotal = 0
    overdueInvoicesTotalOlderThan30Days = 0
    overdueInvoicesPayfast = 0
    overdueInvoicesPaygate = 0
    overdueInvoicesEft = 0
    
    dueInvoicesCount = 0
    dueInvoicesTotal = 0
    
    invoicesWithoutBankDetailsTotalValue = 0
    totalContractValue = 0
    
    // Helper data
    subscriptionRenewalStatus = Object.keys(SubscriptionRenewalStatus)

    constructor(
        private adminService: AdminService, 
        private parkingService: ParkingService, 
        private invoiceService: InvoiceService, 
        private payoutService: PayoutService,
        public enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        // Subscribe to enterprise changes - this will trigger on:
        // 1. Initial load (current enterprise from localStorage)
        // 2. When user changes enterprise via dropdown
        const entSub = this.enterpriseContext.currentEnterpriseKey$.subscribe((key) => {
            // Only reload if the key is actually different from what we loaded
            if (this.currentLoadedEnterpriseKey !== key) {
                this.currentLoadedEnterpriseKey = key
                // Reset loaded tabs so they reload with new enterprise
                this.loadedTabs.clear()
                // Load the active tab
                this.loadTabData(this.activeTab)
            }
        })
        this.componentSubscriptions.push(entSub)
    }
    
    ngOnDestroy(): void {
        this.componentSubscriptions.forEach(sub => sub.unsubscribe())
    }
    
    /**
     * Switch to a different tab and load its data if not already loaded
     */
    selectTab(tab: DashboardTab): void {
        this.activeTab = tab
        this.loadTabData(tab)
    }
    
    /**
     * Load data for a specific tab if not already loaded
     */
    private loadTabData(tab: DashboardTab): void {
        if (this.loadedTabs.has(tab)) {
            return // Already loaded
        }
        
        this.loadedTabs.add(tab)
        
        switch (tab) {
            case 'overview':
                this.loadOverviewData()
                break
            case 'subscriptions':
                this.loadSubscriptionsData()
                break
            case 'invoices':
                this.loadInvoicesData()
                break
            case 'charts':
                this.loadChartsData()
                break
        }
    }
    
    /**
     * Load overview tab data (parkings, users, basic stats)
     */
    private async loadOverviewData(): Promise<void> {
        this.loadingOverview = true
        this.resetOverviewData()
        
        try {
            await Promise.all([
                this.loadParkings(),
                this.loadUsers(),
            ])
        } catch (error) {
            console.error('[Dashboard] Error loading overview:', error)
        } finally {
            this.loadingOverview = false
        }
    }
    
    /**
     * Load subscriptions tab data
     */
    private async loadSubscriptionsData(): Promise<void> {
        this.loadingSubscriptions = true
        this.resetSubscriptionsData()
        
        try {
            await Promise.all([
                this.loadInactiveSubscriptions(),
                this.loadActiveSubscriptions(),
                this.loadExpiringSubscriptions(),
            ])
        } catch (error) {
            console.error('[Dashboard] Error loading subscriptions:', error)
        } finally {
            this.loadingSubscriptions = false
        }
    }
    
    /**
     * Load invoices tab data
     */
    private async loadInvoicesData(): Promise<void> {
        this.loadingInvoices = true
        this.resetInvoicesData()
        
        try {
            await Promise.all([
                this.loadOverdueInvoices(),
                this.loadDueInvoices(),
                this.loadInvoicesNotPaidOut(),
                this.loadInvoicesWithoutBankDetails(),
            ])
        } catch (error) {
            console.error('[Dashboard] Error loading invoices:', error)
        } finally {
            this.loadingInvoices = false
        }
    }
    
    /**
     * Load charts tab data
     */
    private async loadChartsData(): Promise<void> {
        this.loadingCharts = true
        this.resetChartsData()
        
        try {
            await this.loadAllSubscriptionsForCharts()
        } catch (error) {
            console.error('[Dashboard] Error loading charts:', error)
        } finally {
            this.loadingCharts = false
        }
    }
    
    /**
     * Reset overview tab data
     */
    private resetOverviewData(): void {
        this.totalParkings = 0
        this.totalBuildings = 0
        this.totalUnpublishedBuildings = 0
        this.users = 0
        this.usersWithProfile = 0
        this.activeUsers = 0
    }
    
    /**
     * Reset subscriptions tab data
     */
    private resetSubscriptionsData(): void {
        this.activeBays = 0
        this.activeSubscriptions = 0
        this.activeSubscriptionsPayfast = 0
        this.activeSubscriptionsPaygate = 0
        this.activeSubscriptionsEft = 0
        this.activeSubscriptionsManual = 0
        this.totalContractValue = 0
        this.allSubscriptions = []
        this.inactiveSubscriptions = []
        this.expiringSubscriptions = []
    }
    
    /**
     * Reset invoices tab data
     */
    private resetInvoicesData(): void {
        this.invoiceNotPaidOutTotalAmount = 0
        this.invoiceNotPaidOutTotalCount = 0
        this.invoiceNotPaidOutAmount = 0
        this.invoiceNotPaidOutCount = 0
        this.invoiceNotPaidOutProformaAmount = 0
        this.invoiceNotPaidOutProformaCount = 0
        this.overdueInvoicesCount = 0
        this.overdueInvoicesTotal = 0
        this.overdueInvoicesTotalOlderThan30Days = 0
        this.overdueInvoicesPayfast = 0
        this.overdueInvoicesPaygate = 0
        this.overdueInvoicesEft = 0
        this.dueInvoicesCount = 0
        this.dueInvoicesTotal = 0
        this.invoicesWithoutBankDetailsTotalValue = 0
        this.invoicesNotPaidOut = []
        this.invoicesWithoutBankDetails = []
        this.noBankMerchantRefs = []
    }
    
    /**
     * Reset charts tab data
     */
    private resetChartsData(): void {
        this.chartSalesData = []
        this.chartMonthlyData = []
        this.chartBaysData = []
    }
    
    /**
     * Build a Firestore query with optional enterprise filter
     */
    private buildQuery(baseCollection: string, ...constraints: any[]): Query<DocumentData> {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        let q = query(collection(this.adminService.firestore, baseCollection), ...constraints)
        
        if (enterpriseKey) {
            q = query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        
        return q
    }
    
    
    private async loadParkings(): Promise<void> {
        return new Promise((resolve) => {
            this.parkingService.list().pipe(take(1)).subscribe((parkings: Parking[]) => {
                this.totalBuildings = 0
                this.totalParkings = 0
                this.totalUnpublishedBuildings = 0

                parkings.forEach((parking) => {
                    if (parking.numberOfBays && parking.parkingStatus == ParkingStatus.PUBLISHED) {
                        this.totalParkings += Number(parking.numberOfBays)
                        this.totalBuildings += 1
                    } else {
                        this.totalUnpublishedBuildings += 1
                    }
                })
                resolve()
            })
        })
    }
    
    private async loadInactiveSubscriptions(): Promise<void> {
        return new Promise((resolve) => {
            const q = this.buildQuery(
                subscriptionsPath(),
                orderBy('createdAt', 'desc'),
                where('status', 'in', [ContractStatus.paymentInitiated])
            )
            
            collectionData(q, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                this.inactiveSubscriptions = []
                
                documents.forEach((documentData) => {
                    const subscription = new Subscription(documentData)
                    subscription.$key = documentData.documentId

                    // Load related data (fire and forget - UI will update when ready)
                    if (documentData.parkingRef) {
                        docData(documentData.parkingRef).pipe(take(1)).subscribe((data) => {
                            subscription.$parking = new Parking(data)
                            subscription.$parking.$key = documentData.parkingRef.id
                        })
                    }
                    if (documentData.clientRef) {
                        docData(documentData.clientRef).pipe(take(1)).subscribe((data) => {
                            subscription.$client = new Client(data)
                            subscription.$client.$key = documentData.clientRef.id
                        })
                    }
                    if (documentData.merchantRef) {
                        docData(documentData.merchantRef).pipe(take(1)).subscribe((data) => {
                            subscription.$merchant = new Merchant(data)
                            subscription.$merchant.$key = documentData.merchantRef.id
                        })
                    }
                    
                    this.inactiveSubscriptions.push(subscription)
                })
                resolve()
            })
        })
    }
    
    private async loadActiveSubscriptions(): Promise<void> {
        return new Promise((resolve) => {
            const q = this.buildQuery(
                subscriptionsPath(),
                where('status', 'in', [ContractStatus.active])
            )
            
            collectionData(q, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                documents.forEach((documentData) => {
                    const subscription = new Subscription(documentData)
                    subscription.$key = documentData.documentId

                    if (subscription.plan?.term == Term.MONTHLY) {
                        this.activeBays += subscription.baysBooked
                    }

                    this.activeSubscriptions += 1

                    if (subscription.type == SubscriptionType.PAYFAST) {
                        this.activeSubscriptionsPayfast += 1
                    } else if (subscription.type == SubscriptionType.PAYGATE) {
                        this.activeSubscriptionsPaygate += 1
                    } else if (subscription.type == SubscriptionType.EFT) {
                        this.activeSubscriptionsEft += 1
                    } else if (subscription.type == SubscriptionType.MANUAL) {
                        this.activeSubscriptionsManual += 1
                    }

                    if (subscription.endDate) {
                        const monthsLeft = moment(subscription.endDate.toDate()).diff(moment(), 'months')
                        const monthlyFee = subscription.periodFee || 0
                        this.totalContractValue += (monthlyFee / 100) * monthsLeft
                    }
                })
                resolve()
            })
        })
    }
    
    private async loadExpiringSubscriptions(): Promise<void> {
        return new Promise((resolve) => {
            const q = this.buildQuery(
                subscriptionsPath(),
                orderBy('endDate'),
                where('status', 'in', [ContractStatus.active])
            )
            
            collectionData(q, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                this.expiringSubscriptions = []
                
                documents.forEach((documentData) => {
                    const subscription = new Subscription(documentData)
                    subscription.$key = documentData.documentId

                    if (documentData.clientRef) {
                        docData(documentData.clientRef).pipe(take(1)).subscribe((data) => {
                            subscription.$client = new Client(data)
                            subscription.$client.$key = documentData.clientRef.id
                        })
                    }

                    if (documentData.parkingRef) {
                        docData(documentData.parkingRef).pipe(take(1)).subscribe((data) => {
                            subscription.$parking = new Parking(data)
                            subscription.$parking.$key = documentData.parkingRef.id
                        })
                    }

                    if (subscription.endDate) {
                        const expiryDate = moment(subscription.endDate.toDate())
                        const tenDaysFromNow = moment().add(12, 'days')

                        if (expiryDate.isSameOrBefore(tenDaysFromNow, 'day') && 
                            expiryDate.isSameOrAfter(moment(), 'day') && 
                            !subscription.nextDebitDate) {
                            this.expiringSubscriptions.push(subscription)
                        }
                    }
                })

                this.findAndAppendClientPrivate()
                resolve()
            })
        })
    }
    
    private async loadAllSubscriptionsForCharts(): Promise<void> {
        return new Promise((resolve) => {
            const q = this.buildQuery(
                subscriptionsPath(),
                where('status', 'in', [ContractStatus.active, ContractStatus.bouncing])
            )
            
            collectionData(q, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                this.allSubscriptions = documents.map((documentData) => {
                    const subscription = new Subscription(documentData)
                    subscription.$key = documentData.documentId
                    return subscription
                })

                this.buildCharts()
                resolve()
            })
        })
    }
    
    private async loadOverdueInvoices(): Promise<void> {
        return new Promise((resolve) => {
            this.invoiceService.listOverdueInvoices().pipe(take(1)).subscribe((invoices) => {
                this.overdueInvoicesCount = invoices.length
                
                invoices.forEach((invoiceData) => {
                    const invoice = new Invoice(invoiceData)

                    this.overdueInvoicesTotal += invoice.amountTotalDue
                    
                    if (moment(invoice.dueDate.toDate()).isBefore(moment().subtract(30, 'days'))) {
                        this.overdueInvoicesTotalOlderThan30Days += invoice.amountTotalDue
                    }
                    
                    if (invoice.paymentMethodType == SubscriptionType.PAYFAST) {
                        this.overdueInvoicesPayfast += invoice.amountTotalDue
                    } else if (invoice.paymentMethodType == SubscriptionType.PAYGATE) {
                        this.overdueInvoicesPaygate += invoice.amountTotalDue
                    } else if (invoice.paymentMethodType == SubscriptionType.EFT) {
                        this.overdueInvoicesEft += invoice.amountTotalDue
                    }
                })
                resolve()
            })
        })
    }
    
    private async loadDueInvoices(): Promise<void> {
        return new Promise((resolve) => {
            this.invoiceService.listDueInvoices().pipe(take(1)).subscribe((invoices) => {
                this.dueInvoicesCount = invoices.length
                
                invoices.forEach((invoiceData) => {
                    const invoice = new Invoice(invoiceData)
                    this.dueInvoicesTotal += invoice.amountTotalDue
                })
                resolve()
            })
        })
    }
    
    private async loadInvoicesNotPaidOut(): Promise<void> {
        return new Promise((resolve) => {
            this.invoiceService.listInvoicesNotPaidOut().pipe(take(1)).subscribe((invoices) => {
                this.invoicesNotPaidOut = invoices

                invoices.forEach((invoiceData) => {
                    if (invoiceData.payoutRef) {
                        this.invoiceNotPaidOutProformaAmount += invoiceData.periodFinalPayout
                        this.invoiceNotPaidOutProformaCount += 1
                    } else {
                        const invoice = new Invoice(invoiceData)
                        this.invoiceNotPaidOutAmount += invoice.periodFinalPayout
                        this.invoiceNotPaidOutCount += 1
                    }

                    this.invoiceNotPaidOutTotalAmount += invoiceData.periodFinalPayout
                    this.invoiceNotPaidOutTotalCount += 1
                })
                resolve()
            })
        })
    }
    
    private async loadInvoicesWithoutBankDetails(): Promise<void> {
        return new Promise((resolve) => {
            const q = this.buildQuery(
                FirestoreStructure.INVOICES,
                where('status', '==', InvoiceStatus.PAID),
                where('paidOut', '==', false)
            )
            
            collectionData(q, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                this.invoicesWithoutBankDetails = []
                this.invoicesWithoutBankDetailsTotalValue = 0
                this.noBankMerchantRefs = []
                
                let pendingMerchantLoads = documents.filter(d => d.merchantRef).length
                
                if (pendingMerchantLoads === 0) {
                    resolve()
                    return
                }
                
                documents.forEach((documentData) => {
                    const invoice: Invoice = new Invoice(documentData)
                    invoice.$key = documentData.documentId

                    if (documentData.merchantRef) {
                        docData(documentData.merchantRef).pipe(take(1)).subscribe((data) => {
                            invoice.$merchant = new Merchant(data)
                            invoice.$merchant.$key = documentData.merchantRef.id

                            if (!invoice.$merchant.payoutDetails || !invoice.$merchant.payoutDetails.accountNumber) {
                                this.invoicesWithoutBankDetails.push(invoice)

                                if (!this.noBankMerchantRefs.some((ref: any) => 
                                    ref.denormMerchantCompanyName === invoice.denormMerchantCompanyName)) {
                                    this.noBankMerchantRefs.push({
                                        id: documentData.merchantRef.id,
                                        denormMerchantCompanyName: invoice.denormMerchantCompanyName,
                                    })
                                }
                                this.invoicesWithoutBankDetailsTotalValue += invoice.periodFinalPayout
                            }
                            
                            pendingMerchantLoads--
                            if (pendingMerchantLoads === 0) {
                                resolve()
                            }
                        })
                    }
                })
            })
        })
    }
    
    private async loadUsers(): Promise<void> {
        return new Promise((resolve) => {
            const usersRef = collection(this.adminService.firestore, 'clients')
            collectionData(usersRef, { idField: 'documentId' }).pipe(take(1)).subscribe((documents: any[]) => {
                this.users = documents.length
                this.usersWithProfile = documents.filter((doc) => doc.firstName).length

                const currentDate = new Date()
                this.activeUsers = documents.filter((doc) => {
                    if (doc.lastLogin && doc.lastLogin.seconds) {
                        const lastLoginDate = new Date(doc.lastLogin.seconds * 1000)
                        const diffTime = Math.abs(currentDate.getTime() - lastLoginDate.getTime())
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                        return diffDays <= 60
                    }
                    return false
                }).length
                resolve()
            })
        })
    }

    async findAndAppendClientPrivate() {
        try {
            const clientsPrivateRef = query(collection(this.adminService.firestore, adminClientsPath()))
            const clientsPrivateData = await firstValueFrom(collectionData(clientsPrivateRef, { idField: 'documentId' }))

            if (clientsPrivateData) {
                const clientsPrivateMap = new Map(clientsPrivateData.map((clientPrivate: any) => 
                    [clientPrivate.documentId, clientPrivate]
                ))

                this.expiringSubscriptions.forEach((subscription) => {
                    if (subscription.$client) {
                        const clientPrivate = clientsPrivateMap.get(subscription.$client.$key)

                        if (clientPrivate) {
                            const updatedClient = {
                                ...subscription.$client,
                                $email: clientPrivate.email,
                                $phoneNumber: clientPrivate.phoneNumber,
                            }

                            subscription.$client = Object.assign(new Client(), updatedClient)
                        }
                    }
                })
            }
        } catch (error) {
            console.error('[Dashboard] Error loading client private data:', error)
        }
    }

    /**
     * Build all charts from subscription data
     */
    private buildCharts(): void {
        this.buildMonthlyTurnoverChart()
        this.buildBaysChart()
        this.buildContractRevenueChart()
    }

    private buildMonthlyTurnoverChart(): void {
        this.chartMonthlyData = []
        
        const dates: { start: Moment; end: Moment }[] = []
        dates.push({ start: moment().startOf('month'), end: moment().endOf('month') })
        for (let i = 1; i < 12; i++) {
            dates.push({
                start: moment().add(i, 'months').startOf('month'),
                end: moment().add(i, 'months').endOf('month'),
            })
        }

        dates.forEach((chartDate) => {
            let total = 0
            const monthStartDate = moment(chartDate.start)

            this.allSubscriptions.forEach((subscription: Subscription) => {
                if (!subscription.startDate || !subscription.endDate || !subscription.plan) return
                
                const subStartDate = moment(subscription.startDate.toDate())
                const subEndDate = moment(subscription.endDate.toDate())

                let include = monthStartDate.clone().add(1, 'day').isSameOrAfter(subStartDate)
                if (subEndDate.isBefore(monthStartDate)) include = false
                
                if (include) {
                    total += (subscription.plan.feeInCents * subscription.baysBooked) / 100
                }
            })

            this.chartMonthlyData.push({
                name: moment(chartDate.start).format('MMM-YY'),
                series: [{ name: 'Total', value: total }],
            })
        })
    }

    private buildBaysChart(): void {
        this.chartBaysData = []
        
        const dates: { start: Moment; end: Moment }[] = []
        for (let i = 0; i < 12; i++) {
            dates.push({
                start: moment().add(i, 'months').startOf('month'),
                end: moment().add(i, 'months').endOf('month'),
            })
        }

        dates.forEach((chartDate) => {
            let bays = 0
            const monthStartDate = moment(chartDate.start)

            this.allSubscriptions.forEach((subscription: Subscription) => {
                if (!subscription.startDate || !subscription.endDate) return
                
                const subStartDate = moment(subscription.startDate.toDate())
                const subEndDate = moment(subscription.endDate.toDate())

                let include = monthStartDate.clone().add(1, 'day').isSameOrAfter(subStartDate)
                if (subEndDate.isBefore(monthStartDate)) include = false
                
                if (include) {
                    bays += subscription.baysBooked
                }
            })

            this.chartBaysData.push({
                name: moment(chartDate.start).format('MMM-YY'),
                series: [{ name: 'Bays', value: bays }],
            })
        })
    }

    private buildContractRevenueChart(): void {
        this.chartSalesData = []
        
        const dates: { start: Moment; end: Moment }[] = []
        
        // Past 11 months
        for (let i = 11; i > 0; i--) {
            dates.push({
                start: moment().subtract(i, 'months').startOf('month'),
                end: moment().subtract(i, 'months').endOf('month'),
            })
        }
        
        // Current month
        dates.push({ start: moment().startOf('month'), end: moment().endOf('month') })
        
        // Next 3 months
        for (let i = 1; i <= 3; i++) {
            dates.push({
                start: moment().add(i, 'months').startOf('month'),
                end: moment().add(i, 'months').endOf('month'),
            })
        }

        dates.forEach((date) => {
            let total = 0

            this.allSubscriptions.forEach((subscription: Subscription) => {
                if (!subscription.createdAt) return
                
                const createdAt = moment(subscription.createdAt.toDate())
                if (createdAt.isBetween(date.start, date.end, undefined, '[]')) {
                    total += subscription.totalContractValue
                }
            })

            this.chartSalesData.push({
                name: moment(date.end).format('MMM-YY'),
                series: [{ name: 'Total', value: Math.round(total / 100) }],
            })
        })
    }

    async checkIfInvoiceIsInProforma(invoice: Invoice, proformaInvoices: Payout[]) {
        const invoiceRef: any = this.invoiceService.getDocRef(invoice.$key!)
        return proformaInvoices.some((proformaInvoice) => proformaInvoice.invoiceRefs.includes(invoiceRef))
    }

    // Help dialogs
    helpFutureContractValue() {
        Swal.fire(
            'Future Contract Value',
            'The totalContractValue is calculated by looking at each active subscription. For each subscription, it uses the periodFee (monthly fee) and calculates how many months (monthsLeft) are remaining until the endDate of the subscription. It multiplies the monthly fee by the number of months left to get the total value for that subscription. This value is then added to totalContractValue, which represents the total expected revenue from all active subscriptions.'
        )
    }

    helpOverdueInvoices() {
        Swal.fire(
            'Overdue Invoices',
            "This is the list of invoices where we've invoiced the parker, but the parker hasn't paid. Right now, this only includes payfast and manual invoices, because paygate invoices will automatically get marked as paid. In future, this will only include manual invoices."
        )
    }

    helpInvoicesNotPaidOut() {
        Swal.fire(
            'Invoices Not Paid Out',
            "This is all invoices that have been paid to us, but we haven't paid the landlord yet. In the future, this list should only contain the landlords who want to be paid at the end of the month. But this may also include landlords that are missing bank details."
        )
    }

    helpInvoicesMissingBankDetails() {
        Swal.fire('Invoices Missing Bank Details', "This is invoices that Natasha can't pay out because the landlord doesn't have bank details.")
    }

    helpUsers() {
        Swal.fire('Users', 'This is everybody that authenticated with the system, not necessarily everybody that created a parking, and also not everybody that created a profile.')
    }

    helpSalesByMonth() {
        Swal.fire(
            'Sales by month',
            'This value shows the full amount that the parker would pay if they were to pay the whole amount on the creation date of the subscription. This does not take into account the length of the parking subscription, but assumes that parker pays the whole amount in the first month.'
        )
    }

    helpAnticipatedTurnover() {
        Swal.fire('Anticipated Turnover', 'This is the month-on-month turnover that we will receive if no new contracts come in and no contracts are cancelled.')
    }

    formatToK(value: number) {
        if (value >= 1000000) {
            return 'R' + (value / 1000000).toFixed(1) + 'M'
        } else if (value >= 1000) {
            return 'R' + (value / 1000).toFixed(0) + 'K'
        }
        return 'R' + value.toString()
    }
}
